import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronRight, Sparkles } from 'lucide-react';
import { useProfile } from '../context/ProfileContext';

/**
 * ContextualPrompt — generic first-use prompt for surfacing onboarding
 * questions in-context as users discover features, instead of upfront.
 *
 * State is tracked in `profile.seenContextualPrompts[id] = true|'dismissed'`.
 * Once seen, a prompt never reappears — even if the user dismisses without
 * acting. This is intentional: we don't nag.
 *
 * Usage:
 *   <ContextualPrompt
 *     id="injuries-first-pain-check"
 *     visible={!profile.noInjuries && Object.keys(profile.injuries).length === 0}
 *     title="Quick note about your body"
 *     body="We've never asked about injuries…"
 *     action={{ label: 'Tell the coach', href: '/onboarding/full#injuries' }}
 *   />
 */
const ContextualPrompt = ({
  id,
  visible,
  title,
  body,
  action,         // { label, href, onClick? }
  dismissLabel = 'Not now',
  onDismiss,      // optional — e.g. set noInjuries when user picks "No injuries"
  testid,
}) => {
  const { profile, update } = useProfile();
  const navigate = useNavigate();

  const seen = profile.seenContextualPrompts || {};
  if (!visible || seen[id]) return null;

  const markSeen = (state) => {
    update({
      seenContextualPrompts: { ...seen, [id]: state },
    });
  };

  const handleAction = () => {
    markSeen(true);
    if (action.onClick) action.onClick();
    if (action.href) navigate(action.href);
  };

  const handleDismiss = () => {
    if (onDismiss) onDismiss();
    markSeen('dismissed');
  };

  return (
    <div
      className="mb-4 bg-gradient-to-br from-red-600/10 to-zinc-900 border border-enf-accent-fg/30 rounded-xl p-4"
      data-testid={testid || `contextual-prompt-${id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-enf-accent/20 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-enf-accent-fg" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-enf-text-primary mb-1">{title}</div>
          <p className="text-xs text-enf-text-secondary leading-relaxed mb-3">{body}</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleAction}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary text-xs font-semibold rounded-md transition-colors"
              data-testid={`${testid || `contextual-prompt-${id}`}-action`}
            >
              {action.label} <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="px-3 py-1.5 text-xs text-enf-text-secondary hover:text-enf-text-primary transition-colors"
              data-testid={`${testid || `contextual-prompt-${id}`}-dismiss`}
            >
              {dismissLabel}
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Close"
          className="p-1 text-enf-text-muted hover:text-enf-text-primary transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ContextualPrompt;
